/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { noop } from '@amazon/vinyl-util'
import type { Player } from '@/playback/api'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import { markUserSkip } from '@/playback/metrics/userSkipSignal'
import { isAudiobookEntry } from './playbackEntryKind'

/**
 * Audiobook transport next: an audiobook is a single feed entry, so next steps
 * to the next CHAPTER via the player's section-nav surface instead of skipping
 * the feed (GH parity). Returns true when it handled the entry (audiobook) so
 * the caller skips its normal feed-skip path; false for non-audiobooks.
 */
export function stepAudiobookNext(player: Player, entry: PlaylistEntry | null): boolean {
    if (!isAudiobookEntry(entry)) return false
    markUserSkip('next')
    void Promise.resolve(player.sections.nextSection()).catch(noop)
    return true
}

/**
 * Audiobook transport previous, preserving the ≤3s rule against the CHAPTER: at
 * the chapter start (position < 3s) step to the previous chapter; past 3s
 * restart the current chapter (`seekTo` is section-relative on the Audible
 * engine). Only the go-to-previous branch counts as a user skip. Returns true
 * when it handled the entry; false for non-audiobooks.
 */
export function stepAudiobookPrevious(player: Player, entry: PlaylistEntry | null): boolean {
    if (!isAudiobookEntry(entry)) return false
    if (player.sections.getPositionInSection() < 3) {
        markUserSkip('previous')
        void Promise.resolve(player.sections.previousSection()).catch(noop)
    } else {
        player.seekTo(0).catch(noop)
    }
    return true
}
